import React, { useEffect, useState, FC } from 'react';
import { showAppsData } from './ShowAppsData.tsx';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import MobileStepper from '@mui/material/MobileStepper';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

interface ShowAppsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShowAppsDialog: FC<ShowAppsDialogProps> = ({ isOpen, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isFading, setIsFading] = useState(false);

  const maxSteps = showAppsData.length;
  const isLastStep = activeStep === maxSteps - 1;
  const currentStepData = showAppsData[activeStep];

  // Handling the "next step" transition
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isFading) {
      timer = setTimeout(() => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setIsFading(false);
      }, 300);
    }
    return () => clearTimeout(timer);
  }, [isFading]);

  // Resetting the state when the dialog closes - Get rid of this after implementing what will happen after closing this dialog
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isOpen) {
      timer = setTimeout(() => {
        setActiveStep(0);
        setIsFading(false);
      }, 200);
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleNext = () => {
    if (isLastStep) {
      onClose();
    } else {
      setIsFading(true);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullScreen>
      <DialogWrapper>
        <Box>
          {/* Header */}
          <DialogHeader>
            <img
              src='/images/IconOnly_Transparent_NoBuffer.png'
              alt='Mashlom Logo'
              style={{ height: '25px', margin: '8px' }}
            />
            <IconButton onClick={onClose}>
              <CloseIcon htmlColor='#103C6E' />
            </IconButton>
          </DialogHeader>

          {/* Body */}
          <>
            <DialogTitle>{currentStepData.section}</DialogTitle>
            <DialogBody>
              <PhoneMockup
                component='video'
                src={currentStepData.animationPath}
                autoPlay
                loop
                playsInline
                isFading={isFading}
              />
              <AvatarAndTextContainer>
                <TextBubble>
                  <p
                    style={{
                      fontWeight: 'bold',
                      textAlign: 'center',
                      margin: 0,
                    }}
                  >
                    {currentStepData.title}
                  </p>
                  <p style={{ textAlign: 'right', margin: 0 }}>
                    {currentStepData.text}
                  </p>
                </TextBubble>
                <DoctorAvatar
                  component='video'
                  src='/animations/doctor-points-up.mp4'
                  autoPlay
                  loop
                  playsInline
                />
              </AvatarAndTextContainer>
            </DialogBody>
          </>
        </Box>
        {/* Footer */}
        <Footer>
          {isLastStep && (
            <LastMessage>
              חסר לכם משהו?
              צרו איתנו קשר!
            </LastMessage>
          )}
          <Stepper
            variant='dots'
            position='static'
            steps={maxSteps}
            activeStep={activeStep}
            backButton={null}
            nextButton={null}
          />
          <NextButton
            size='large'
            variant='contained'
            onClick={handleNext}
            fullWidth
          >
            {isLastStep ? 'סיום' : 'הבא'}
          </NextButton>
        </Footer>
      </DialogWrapper>
    </Dialog>
  );
};

export default ShowAppsDialog;

/*-------------------STYLES-------------------*/
const FONT = '"Rubik", sans-serif';

const DialogWrapper = styled(Box)(({ theme }) => ({
  marginBlock: '1vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: 'calc(100% - 2vh)',
  paddingBottom: '2vh',
}));

const DialogHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px',
});


const DialogTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 'bold',
  color: '#103C6E',
  fontFamily: FONT,
  fontSize: '1.6rem',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(6),
  },
}));

const DialogBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  flexDirection: 'column',
  alignItems: 'center',
  paddingBottom: '1rem',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingBottom: 0,
  },
}));

const PhoneMockup = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFading',
})<{ isFading?: boolean }>(({ theme, isFading }) => ({
  margin: '0 auto',  
  height: '40dvh',
  zIndex: 0,
  transition: 'opacity 300ms ease-in-out',
  opacity: isFading ? 0 : 1,
  [theme.breakpoints.up('md')]: {
    height: '60dvh',
  },
}));

const AvatarAndTextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
  marginBlock: '1rem',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    position: 'absolute',
    width: 'auto',
    top: '50%',
    left: '15%',
    transform: 'translateY(-50%)',
    marginTop: 0,
  },
}));

const TextBubble = styled(Box)(({ theme }) => ({
  backgroundColor: '#EEFFFD',
  borderRadius: '8px',
  padding: '15px 10px',
  color: '#103C6E',
  fontSize: '1rem',
  fontFamily: FONT,
  width: '60%',
  zIndex: 1,
  marginRight: '-20px',
  [theme.breakpoints.up('md')]: {
    maxWidth: '15vw',
    marginRight: 0,
    marginLeft: '-20px',
    order: 2,
    zIndex: 1,
  },
}));

const DoctorAvatar = styled(Box)(({ theme }) => ({
  borderRadius: '50%',
  width: '120px',
  zIndex: 2,
  [theme.breakpoints.up('md')]: {
    width: '12vw',
    maxWidth: '150px',
    order: 1,
    zIndex: 2,
  },
}));

const Footer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  paddingInline: '1rem',
}) as typeof Box;

const Stepper = styled(MobileStepper)({
  gap: 7,
  justifyContent: 'center',
  '& .MuiMobileStepper-dot': {
    backgroundColor: '#E0E0E0',
    width: 10,
    height: 10,
  },
  '& .MuiMobileStepper-dotActive': { backgroundColor: '#103C6E' },
}) as typeof MobileStepper;

const NextButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  backgroundColor: '#103C6E',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 'bold',
  width: '100%',
  maxWidth: '400px',
  '&:hover': { backgroundColor: '#1E487B' },
}));

const LastMessage = styled(Typography)({
  textAlign: 'center',
  fontWeight: 'bold',
  fontSize: '0.9rem',
  color: '#103C6E',
  fontFamily: FONT,
}) as typeof Typography;
